import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal, Pencil, PiggyBank, Plus, Printer, Receipt, Trash2, TrendingUp, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import {
  useDeleteEngCost,
  useEngCosts,
  useEngPrints,
  useUpdateEngProject,
  type EngCost,
  type EngProject,
} from '@/features/engineering/hooks';
import { ENG_COST_CATEGORIES, engCostCategoryLabel } from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { centsToInputDollars, formatCurrency, formatDateOnly, inputDollarsToCents } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { BudgetBar, StatCard } from './EngParts';
import { EngCostDialog } from './EngCostDialog';
import { summarisePrints } from './engPrintUtils';

const PRINTS_BUCKET = '__prints__';
const EMPTY_COSTS: EngCost[] = [];

interface EngBudgetTabProps {
  project: EngProject;
  canEdit: boolean;
  onGoToTab: (tab: string) => void;
}

/**
 * Budget vs actuals. Spend = logged cost entries + material cost of
 * every 3D print in the print log, broken down by category.
 */
export function EngBudgetTab({ project, canEdit, onGoToTab }: EngBudgetTabProps) {
  const { data: costs, isLoading: costsLoading } = useEngCosts(project.id);
  const { data: prints, isLoading: printsLoading } = useEngPrints(project.id);
  const remove = useDeleteEngCost();
  const [dialog, setDialog] = useState<{ cost: EngCost | null } | null>(null);
  const [budgetOpen, setBudgetOpen] = useState(false);

  const costList = costs ?? EMPTY_COSTS;
  const printSummary = useMemo(() => summarisePrints(prints ?? []), [prints]);

  const totals = useMemo(() => {
    const costTotal = costList.reduce((s, c) => s + c.amount_cents, 0);
    const spent = costTotal + printSummary.totalCents;
    const budget = project.budget_cents;
    return {
      costTotal,
      spent,
      budget,
      remaining: budget != null ? budget - spent : null,
      ratio: budget && budget > 0 ? spent / budget : null,
    };
  }, [costList, printSummary.totalCents, project.budget_cents]);

  const breakdown = useMemo(() => {
    const byCategory = new Map<string, number>();
    for (const c of costList) byCategory.set(c.category, (byCategory.get(c.category) ?? 0) + c.amount_cents);
    const rows: Array<{ key: string; label: string; cents: number; count: number; isPrints?: boolean }> = [];
    for (const cat of ENG_COST_CATEGORIES) {
      const cents = byCategory.get(cat);
      if (!cents) continue;
      rows.push({
        key: cat,
        label: engCostCategoryLabel(cat),
        cents,
        count: costList.filter((c) => c.category === cat).length,
      });
    }
    if (printSummary.totalCents > 0 || printSummary.total > 0) {
      rows.push({
        key: PRINTS_BUCKET,
        label: '3D prints (print log)',
        cents: printSummary.totalCents,
        count: printSummary.total,
        isPrints: true,
      });
    }
    return rows.sort((a, b) => b.cents - a.cents);
  }, [costList, printSummary]);

  const handleDelete = async (cost: EngCost) => {
    if (!window.confirm(`Delete “${cost.description}”?`)) return;
    try {
      await remove.mutateAsync({ id: cost.id, projectId: project.id });
      toast.success('Cost deleted.');
    } catch (err) {
      toast.error(formatError(err, 'Could not delete the cost.'));
    }
  };

  const loading = costsLoading || printsLoading;
  const over = totals.remaining != null && totals.remaining < 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Budget"
          value={totals.budget != null ? formatCurrency(totals.budget, { whole: true }) : '—'}
          icon={PiggyBank}
          hint={
            canEdit ? (
              <button type="button" className="inline-flex items-center gap-1 hover:underline" onClick={() => setBudgetOpen(true)}>
                <Pencil className="h-3 w-3" />
                {totals.budget != null ? 'Change budget' : 'Set a budget'}
              </button>
            ) : totals.budget == null ? (
              'No budget set'
            ) : undefined
          }
        />
        <StatCard
          label="Spent to date"
          value={formatCurrency(totals.spent)}
          icon={Wallet}
          hint={
            <>
              {totals.ratio != null && (
                <span className={cn(over && 'font-medium text-fresh-salmon-dark')}>
                  {Math.round(totals.ratio * 100)}% of budget
                </span>
              )}
              {totals.ratio == null && `${costList.length} cost entr${costList.length === 1 ? 'y' : 'ies'}`}
              <BudgetBar spentCents={totals.spent} budgetCents={totals.budget} className="mt-2" />
            </>
          }
        />
        <StatCard
          label={over ? 'Over budget by' : 'Remaining'}
          value={totals.remaining != null ? formatCurrency(Math.abs(totals.remaining)) : '—'}
          icon={TrendingUp}
          hint={totals.remaining == null ? 'Set a budget to track this' : over ? 'Spend exceeds the budget' : 'Left to spend'}
          className={over ? 'border-fresh-salmon/60' : undefined}
        />
        <StatCard
          label="3D prints"
          value={formatCurrency(printSummary.totalCents)}
          icon={Printer}
          hint={
            <button type="button" className="hover:underline" onClick={() => onGoToTab('prints')}>
              {printSummary.total} print{printSummary.total === 1 ? '' : 's'} logged · open print log
            </button>
          }
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card className="min-w-0">
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-3">
            <div>
              <CardTitle className="text-base">Cost entries</CardTitle>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Hardware, materials, outsourced work and anything else bought for this project.
              </p>
            </div>
            {canEdit && (
              <Button size="sm" onClick={() => setDialog({ cost: null })}>
                <Plus className="mr-2 h-4 w-4" />
                Log a cost
              </Button>
            )}
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {loading ? (
              <div className="space-y-2 px-6 pb-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : costList.length === 0 ? (
              <div className="px-6 pb-6">
                <EmptyState
                  icon={Receipt}
                  title="No costs logged yet"
                  className="py-10"
                  action={
                    canEdit ? (
                      <Button size="sm" onClick={() => setDialog({ cost: null })}>
                        <Plus className="mr-2 h-4 w-4" />
                        Log the first cost
                      </Button>
                    ) : undefined
                  }
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[110px]">Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-[150px]">Category</TableHead>
                      <TableHead className="w-[140px] text-right">Qty × unit</TableHead>
                      <TableHead className="w-[120px] text-right">Amount</TableHead>
                      <TableHead className="w-[48px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costList.map((cost) => (
                      <TableRow
                        key={cost.id}
                        className="cursor-pointer"
                        onClick={() => setDialog({ cost })}
                      >
                        <TableCell className="whitespace-nowrap text-sm">{formatDateOnly(cost.incurred_on)}</TableCell>
                        <TableCell>
                          <div className="min-w-[180px]">
                            <div className="text-sm font-medium text-foreground">{cost.description}</div>
                            <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                              {cost.vendor && <span>{cost.vendor}</span>}
                              {cost.reference && <span className="font-mono">{cost.reference}</span>}
                              {cost.item && (
                                <Link
                                  to={`/catalog/items/${cost.item.id}`}
                                  className="hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <span className="font-mono">{cost.item.sku}</span> {cost.item.name}
                                </Link>
                              )}
                              {cost.print && (
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 hover:underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onGoToTab('prints');
                                  }}
                                >
                                  <Printer className="h-3 w-3" />
                                  {cost.print.title}
                                </button>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="whitespace-nowrap">
                            {engCostCategoryLabel(cost.category)}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right text-sm tabular-nums text-muted-foreground">
                          {cost.quantity != null && cost.unit_cents != null
                            ? `${cost.quantity} × ${formatCurrency(cost.unit_cents)}`
                            : cost.quantity != null
                              ? `×${cost.quantity}`
                              : '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right text-sm font-medium tabular-nums">
                          {formatCurrency(cost.amount_cents)}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Cost actions">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => setDialog({ cost })}>
                                <Pencil className="mr-2 h-3.5 w-3.5" />
                                {canEdit ? 'Edit cost' : 'View details'}
                              </DropdownMenuItem>
                              {canEdit && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onSelect={() => handleDelete(cost)}
                                  >
                                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                                    Delete cost
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={4} className="text-right text-sm text-muted-foreground">
                        Cost entries
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium tabular-nums">
                        {formatCurrency(totals.costTotal)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={4} className="text-right text-sm text-muted-foreground">
                        3D prints (from the print log)
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium tabular-nums">
                        {formatCurrency(printSummary.totalCents)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    <TableRow className="border-t-2 hover:bg-transparent">
                      <TableCell colSpan={4} className="text-right text-sm font-medium text-foreground">
                        Total spend
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold tabular-nums">
                        {formatCurrency(totals.spent)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Where it went</CardTitle>
          </CardHeader>
          <CardContent>
            {breakdown.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing spent yet.</p>
            ) : (
              <ul className="space-y-3">
                {breakdown.map((row) => {
                  const share = totals.spent > 0 ? row.cents / totals.spent : 0;
                  return (
                    <li key={row.key}>
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="flex min-w-0 items-center gap-1.5 truncate text-foreground">
                          {row.isPrints && <Printer className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                          {row.label}
                        </span>
                        <span className="shrink-0 tabular-nums">{formatCurrency(row.cents)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary" aria-hidden>
                          <div
                            className={cn('h-full rounded-full', row.isPrints ? 'bg-info' : 'bg-bright-moss-dark')}
                            style={{ width: `${Math.max(share * 100, 2)}%` }}
                          />
                        </div>
                        <span className="w-16 text-right text-xs tabular-nums text-muted-foreground">
                          {Math.round(share * 100)}% · {row.count}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {dialog && (
        <EngCostDialog
          open
          onOpenChange={(o) => !o && setDialog(null)}
          projectId={project.id}
          cost={dialog.cost}
          canDelete={canEdit}
        />
      )}
      <SetBudgetDialog project={project} open={budgetOpen} onOpenChange={setBudgetOpen} />
    </div>
  );
}

function SetBudgetDialog({
  project,
  open,
  onOpenChange,
}: {
  project: EngProject;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateEngProject();
  const [budget, setBudget] = useState('');

  useEffect(() => {
    if (open) setBudget(centsToInputDollars(project.budget_cents));
  }, [open, project.budget_cents]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    let cents: number | null;
    try {
      cents = inputDollarsToCents(budget);
    } catch (err) {
      toast.error(formatError(err));
      return;
    }
    try {
      await update.mutateAsync({ id: project.id, patch: { budget_cents: cents } });
      toast.success(cents == null ? 'Budget cleared.' : 'Budget updated.');
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err, 'Could not update the budget.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Project budget</DialogTitle>
          <DialogDescription>Leave blank to track spend without a ceiling.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="eng-budget">Budget</Label>
            <Input
              id="eng-budget"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="$ 0.00"
              inputMode="decimal"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
